import videojs from 'video.js';
import { KeyboardEvent } from 'react';
import { IKeySystemConfig } from 'src/interfaces/Drm';
import { DEVICE_CONNECTION } from 'src/interfaces/enums';
import { MessageEventData } from 'src/interfaces/MessageEvent';
import { isServer } from '../envUtils';

const browserUtils = {
  detectEdge,
  isApple,
  detectAndroid,
  detectIE,
  detectIOS,
  detectSafari,
  getBrowserLanguage,
  requestKeySystem,
  reloadPage,
  getBrowserInfo,
  getMouseOrTouchPosition,
  getTouchPosition,
  getMousePosition,
  isFullscreen,
  enterFullscreen,
};

export default browserUtils;

export function getMousePosition(event: MouseEvent) {
  return {
    x: event.pageX,
    y: event.pageY,
  };
}

export function getTouchPosition(event: TouchEvent) {
  const [touch] = event.touches ?? { x: 0, y: 0 };

  return {
    x: touch.pageX,
    y: touch.pageY,
  };
}

export function getMouseOrTouchPosition(event: MouseEvent | TouchEvent) {
  if (event instanceof MouseEvent) return browserUtils.getMousePosition(event);
  else return browserUtils.getTouchPosition(event);
}

export function getBrowserLanguage() {
  return navigator.language;
}

export function reloadPage() {
  window.location.reload();
}

export async function requestKeySystem(key: string, config: IKeySystemConfig[]) {
  try {
    await window.navigator.requestMediaKeySystemAccess(key, config);
    return Promise.resolve(key);
  } catch (e) {
    console.error(`${key} not supported`);
  }

  return void 0;
}

export function detectSafari() {
  return videojs.browser.IS_SAFARI;
}

export function detectAndroid() {
  return videojs.browser.IS_NATIVE_ANDROID || videojs.browser.IS_ANDROID;
}

export function detectIOS() {
  return videojs.browser.IS_IOS;
}

export function detectEdge() {
  return videojs.browser.IS_EDGE;
}

export function detectSamsung() {
  const { userAgent } = window.navigator;

  return /SamsungBrowser/i.test(userAgent);
}

export function detectMi() {
  const { userAgent } = window.navigator;

  return /MiuiBrowser/i.test(userAgent);
}

export function detectIE() {
  return videojs.browser.IE_VERSION;
}

export function detectMobile() {
  return detectAndroid() || detectIOS();
}

export function isApple() {
  return detectIOS() || detectSafari();
}

export function getBrowserInfo() {
  return navigator.userAgent;
}

export function detectConnectionType() {
  return detectAndroid() || detectIOS() ? DEVICE_CONNECTION.WIRELESS : DEVICE_CONNECTION.CABLE;
}

export const postMessage = (message: MessageEventData) => {
  top?.postMessage(JSON.stringify(message), '*');
};

export const isPictureInPictureSupported = () => {
  return !isServer() && document.pictureInPictureEnabled;
};

export const isPictureInPicture = () => {
  return !isServer() && !!document.pictureInPictureElement;
};

export const exitPictureInPicture = () => {
  return !isServer() && document.exitPictureInPicture();
};

export function enterFullscreen() {
  if (browserUtils.detectIOS()) {
    document.querySelector('video')?.webkitEnterFullscreen?.();
    return;
  }

  const elem = document.documentElement;

  if (elem.requestFullscreen) {
    elem.requestFullscreen();
  } else if (elem.msRequestFullscreen) {
    elem.msRequestFullscreen();
  } else if (elem.mozRequestFullScreen) {
    elem.mozRequestFullScreen();
  } else if (elem.webkitRequestFullscreen) {
    elem.webkitRequestFullscreen();
  }
}

export function exitFullscreen() {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.webkitExitFullscreen) {
    /* Safari */
    document.webkitExitFullscreen();
  } else if (document.msExitFullscreen) {
    /* IE11 */
    document.msExitFullscreen();
  }
}

export function isFullscreen() {
  return !!(
    document.fullscreenElement ||
    document.msFullscreenElement ||
    document.mozFullScreenElement ||
    document.webkitFullscreenElement
  );
}

export type KeyDownOptions = {
  keys: string[];
};

export function stopKeyDownPropagation(event: KeyboardEvent, { keys }: KeyDownOptions) {
  if (keys.includes(event.key)) event.stopPropagation();
}

export function dispatchEventToWindow(eventName: string) {
  window.dispatchEvent(new Event(eventName));
}
